import React, { useEffect, useState } from "react";
import { Mail, Send, X } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

/**
 * Send invoice/quote email dialog
 * props:
 *  - kind: "invoice" | "quote"
 *  - docId, docNumber
 *  - clientEmail (prefill)
 *  - clientName
 */
export default function SendEmailDialog({
  kind,
  docId,
  docNumber,
  clientEmail,
  clientName,
}) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(clientEmail || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) setTo(clientEmail || "");
  }, [open, clientEmail]);

  const label =
    kind === "invoice"
      ? lang === "es"
        ? "Enviar factura"
        : "Enviar factura"
      : lang === "es"
      ? "Enviar presupuesto"
      : "Enviar pressupost";

  const placeholder =
    lang === "es"
      ? `Hola ${clientName || ""}, adjunto el documento. Cualquier duda, contacta conmigo.`
      : `Hola ${clientName || ""}, t'adjunto el document. Per qualsevol dubte, contacta amb mi.`;

  const onSend = async () => {
    if (!to) {
      toast.error(lang === "es" ? "Falta destinatario" : "Falta destinatari");
      return;
    }
    setSending(true);
    try {
      const path = kind === "invoice" ? "invoices" : "quotes";
      await api.post(`/${path}/${docId}/send-email`, {
        to,
        message: message || undefined,
      });
      toast.success(
        lang === "es"
          ? `Enviado a ${to}`
          : `Enviat a ${to}`
      );
      setOpen(false);
      setMessage("");
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="border-white/10"
        data-testid={`${kind}-send-email-btn`}
      >
        <Mail size={14} className="mr-1.5" /> {label}
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150"
          onClick={() => !sending && setOpen(false)}
        >
          <div
            className="w-full max-w-lg bg-[#0B1220] border border-white/10 rounded-2xl shadow-2xl p-6 animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
            data-testid={`${kind}-send-email-dialog`}
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-white">
                    {label}
                  </h3>
                  <p className="text-xs text-slate-400">Nº {docNumber}</p>
                </div>
              </div>
              <button
                onClick={() => !sending && setOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-slate-400"
                disabled={sending}
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 mb-1.5 block">
                  {lang === "es" ? "Para" : "Per a"}
                </label>
                <Input
                  type="email"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="client@exemple.com"
                  data-testid="send-email-to-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 mb-1.5 block">
                  {lang === "es" ? "Mensaje (opcional)" : "Missatge (opcional)"}
                </label>
                <Textarea
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={placeholder}
                  data-testid="send-email-message-input"
                  className="bg-white/5 border-white/10 text-white resize-none"
                />
                <p className="text-[11px] text-slate-500 mt-1.5">
                  {lang === "es"
                    ? "Se adjuntará el PDF automáticamente."
                    : "S'adjuntarà el PDF automàticament."}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-6">
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={sending}
                className="text-slate-400 hover:text-white"
              >
                {lang === "es" ? "Cancelar" : "Cancel·lar"}
              </Button>
              <Button
                onClick={onSend}
                disabled={sending || !to}
                data-testid="send-email-submit-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Send size={14} className="mr-1.5" />
                {sending
                  ? lang === "es"
                    ? "Enviando..."
                    : "Enviant..."
                  : lang === "es"
                  ? "Enviar"
                  : "Enviar"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
